import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { gbp, ukDate, ukDateTime } from "../lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export const GLOSSARY_TERMS = {
  Proforma: "The original approved figures, locked as the baseline (V1). A re-base creates Proforma V2; V1 stays visible.",
  Current: "Latest forecast.",
  Actual: "Achieved or paid to date.",
  Variance: "Current vs Proforma.",
  "Equity requirement": "Total equity committed to the project per the proforma – the ‘Out’ figure.",
  "Equity deployed": "Equity actually paid in to date.",
  "Equity to deploy": "Equity requirement minus equity deployed (replaces 'Still to go in').",
  "Drawdown request": "A request for funds, awaiting approval.",
  "Drawdown approved": "Approved by GM and then Afzal, awaiting payment.",
  "Cash in": "Money received (equity + profit) – the ‘In’ figure.",
  "Long stop date": "Date the JV partner loses the profit for failing to perform.",
  Seesaw: "Erosion of profits if the project does not perform against the agreed timeline.",
  "Deployed as": "How equity goes into a project: Equity payment to SPV, Loan, or Both. Set at project level.",
  "Net of VAT": "All figures exclude VAT unless marked otherwise.",
  "Not set": "Shown in amber where a required figure is missing.",
};
const G = (t, extra = "") => `${t} – ${GLOSSARY_TERMS[t]}${extra ? ` ${extra}` : ""}`;

export const DEFS = {
  equity: G("Equity requirement"),
  profit: "Forecast profit – the profit expected from the project.",
  irr: "IRR – the annual return on the equity, Current vs Proforma (relative: a 20% proforma turns red below 18%).",
  return_date: "Equity return date – when the equity is expected back. Amber 1–3 months late, red over 3.",
  profit_date: "Profit date – when the profit is expected. Amber 1–3 months late, red over 3.",
  duration: "Duration – months from the first drawdown (or proforma signing) to the equity return date, calculated automatically.",
  long_stop: G("Long stop date"),
  Proforma: G("Proforma"),
  Current: G("Current"),
  Actual: G("Actual"),
  Variance: G("Variance"),
  equity_deployed: G("Equity deployed"),
  equity_to_deploy: G("Equity to deploy"),
  forecast_profit: "Forecast profit – the current forecast of profit from your projects.",
  current_irr: "Current IRR – the latest forecast annual return on the equity (weighted by each project's equity requirement).",
  cash_in: G("Cash in"),
  seesaw: G("Seesaw"),
  not_set: G("Not set"),
  vat: G("Net of VAT"),
};

export const GLOSSARY = [...Object.entries(GLOSSARY_TERMS),
  ["Forecast profit", "The current forecast of profit from your projects."],
  ["Current IRR", "The latest forecast annual return on the equity, weighted by each project's equity requirement."],
  ["RAG", "Green on or ahead of proforma; amber dates 1–3 months late or figures up to 10% worse; red dates over 3 months late or figures more than 10% worse. A project takes the colour of its worst line."]];

export const InfoTip = ({ k, text, testid }) => (
  <Popover><PopoverTrigger asChild><button type="button" className="ml-1 inline-flex align-middle text-muted-foreground hover:text-primary" aria-label="Definition" data-testid={testid || `info-${k}`}><Info className="h-3.5 w-3.5" /></button></PopoverTrigger>
    <PopoverContent className="w-72 text-xs" data-testid={`${testid || `info-${k}`}-text`}>{text || DEFS[k]}</PopoverContent></Popover>
);

const COLOUR = { red: "bg-primary", amber: "bg-[hsl(var(--warning))]", green: "bg-[hsl(var(--positive))]" };
export const Rag = ({ c, testid }) => <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${COLOUR[c]}`} title={c} data-testid={testid} />;

function fmt(kind, v) {
  if (v == null) return null;
  if (kind === "gbp") return gbp(v);
  if (kind === "pct") return `${(v * 100).toFixed(1)}%`;
  if (kind === "date") return ukDate(v);
  return `${v}`;
}

function varText(l) {
  if (l.not_set || l.variance == null) return "—";
  const v = l.variance;
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  const arrow = v > 0 ? "▲ " : v < 0 ? "▼ " : "";
  if (l.kind === "gbp") return Math.abs(v) < 1 ? "£0" : `${arrow}${sign}${gbp(Math.abs(v))}`;
  if (l.kind === "pct") return `${arrow}${sign}${Math.abs(v * 100).toFixed(1)} pts`;
  if (l.kind === "date" || l.kind === "months") return Math.round(v) === 0 ? "On time" : `${arrow}${sign}${Math.abs(Math.round(v))} mo`;
  return v;
}

const tone = (l) => (l.not_set || l.variance == null || (l.kind === "gbp" && Math.abs(l.variance) < 1) ? "text-muted-foreground" : l.flag === "red" ? "text-primary font-semibold" : l.flag === "amber" ? "text-[hsl(var(--warning))] font-semibold" : "text-[hsl(var(--positive))]");
const NotSet = () => <span className="rounded bg-[hsl(var(--warning)/0.18)] px-1.5 py-0.5 text-[11px] font-semibold text-[hsl(var(--warning))]">Not set</span>;

export function VarianceLines({ pid, lines, testid = "variance-lines" }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="dt w-full min-w-[720px] text-sm">
        <thead><tr className="text-left text-xs">
          <th>Line</th>{["Proforma", "Current", "Actual"].map((h) => <th key={h} className="num sl">{h}<InfoTip k={h} testid={`info-col-${h.toLowerCase()}`} /></th>)}
          <th className="num gl">Variance (Current vs Proforma)<InfoTip k="Variance" testid="info-col-variance" /></th><th>RAG</th></tr></thead>
        <tbody>{lines.map((l) => (
          <tr key={l.key} data-testid={`vline-${l.key}`}>
            <td><button className="text-left font-semibold hover:text-primary hover:underline" onClick={() => setOpen(open === l.key ? null : l.key)} data-testid={`vline-${l.key}-open`}>{l.label}</button><InfoTip k={l.key} testid={`info-line-${l.key}`} />
              {open === l.key && <div className="mt-1"><LineHistory pid={pid} k={l.key} /></div>}</td>
            <td className="num sl">{fmt(l.kind, l.proforma) ?? <NotSet />}</td>
            <td className="num sl">{fmt(l.kind, l.current) ?? <NotSet />}</td>
            <td className="num sl">{fmt(l.kind, l.actual) ?? "—"}</td>
            <td className={`num gl ${tone(l)}`} data-testid={`vline-${l.key}-variance`}>{varText(l)}</td>
            <td><Rag c={l.flag} testid={`vline-${l.key}-rag`} /></td>
          </tr>))}</tbody>
      </table>
      <p className="mt-2 text-[11px] text-muted-foreground">All figures net of VAT unless marked. Click a line to see its change history.</p>
    </div>
  );
}

function LineHistory({ pid, k }) {
  const [h, setH] = useState(null);
  useEffect(() => { api.get(`/projects/${pid}/line-history/${k}`).then((r) => setH(r.data.history)).catch((e) => setH(errMsg(e))); }, [pid, k]);
  if (!h) return <span className="text-xs text-muted-foreground">Loading…</span>;
  if (typeof h === "string") return <span className="text-xs text-primary">{h}</span>;
  return (
    <ul className="max-w-md space-y-1 rounded-md bg-muted/60 p-2 text-xs" data-testid={`line-history-${k}`}>
      {!h.length ? <li>No changes recorded yet.</li> : h.slice(0, 8).map((x, i) => <li key={i}><b>{x.value != null ? String(x.value).slice(0, 40) : "—"}</b> · {x.by || "System"} · {x.at ? ukDateTime(x.at) : ""} <span className="text-muted-foreground">– {x.reason || x.source}</span></li>)}
    </ul>
  );
}

